import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../lib/apiClient';
import { useTreatmentCatalogConfig } from '../lib/treatmentCatalog';
import { formatMoney as formatOrgMoney } from '../i18n/orgFormat';

type StockUsage = {
  itemId?: string;
  billingProductId?: string;
  lotId?: string;
  itemName: string;
  plannedQuantity?: number;
  actualQuantity: number;
  unit?: string;
  brand?: string;
  lotNumber?: string;
  deviationReason?: string;
};

type CompletionPayload = {
  appointment: {
    id: string;
    status: string;
    paymentStatus?: PaymentStatusLike;
    patient: { name: string };
    provider: { name: string };
    location?: { id: string; name: string } | null;
    appointmentType: { id: string; name: string; price: number; currency: string; clinicalSafetyRules?: Record<string, unknown> };
    billingService?: { id: string; code?: string | null; name: string; category?: string | null; price: number } | null;
  };
  availableServices?: Array<{ id: string; code: string; name: string; category?: string | null; price: number; active: boolean }>;
  defaultStockRecipe: StockUsage[];
  completion?: {
    billingServiceId?: string | null;
    performedServiceName: string;
    performedServices?: Array<{ serviceId?: string; code: string; name: string; quantity: number; unitPrice: number }>;
    notes?: string | null;
    checklist?: Array<{ label: string; checked: boolean }>;
    stockUsage?: StockUsage[];
    paymentStatus: PaymentStatus;
    expectedAmount: number;
    finalAmount: number;
  } | null;
};

type PaymentStatus = 'UNPAID' | 'DUE_AT_PARENT' | 'PAID_AT_PARENT' | 'PACKAGE_REDEEMED' | 'COMPLIMENTARY' | 'WAIVED' | 'NOT_REQUIRED';

type InventorySummaryRow = {
  id: string;
  name: string;
  baseUnit: string;
  strength?: string | null;
  packageSize?: string | null;
  lotId?: string | null;
  lotNumber?: string | null;
  brand?: string | null;
  available: number;
};

type ServicePerformedOption = {
  value: string;
  label: string;
  supportingText: string;
  price: number;
  code: string;
  billingServiceId?: string;
};

const paymentStatuses: PaymentStatus[] = ['UNPAID', 'DUE_AT_PARENT', 'PAID_AT_PARENT', 'PACKAGE_REDEEMED', 'COMPLIMENTARY', 'WAIVED', 'NOT_REQUIRED'];
const paymentStatusLabels: Record<PaymentStatus, string> = {
  UNPAID: 'Payment due',
  DUE_AT_PARENT: 'Pay on invoice',
  PAID_AT_PARENT: 'Already paid on invoice',
  PACKAGE_REDEEMED: 'Covered by package',
  COMPLIMENTARY: 'Complimentary',
  WAIVED: 'Waived',
  NOT_REQUIRED: 'No payment required',
};

type PaymentStatusLike = PaymentStatus | { status?: string | null } | null | undefined;
function normalizeTreatmentPaymentStatus(value: PaymentStatusLike): PaymentStatus {
  if (typeof value === 'string' && paymentStatuses.includes(value as PaymentStatus)) {
    return value as PaymentStatus;
  }

  const summaryStatus = typeof value === 'object' && value && typeof value.status === 'string' ? value.status : null;
  switch (summaryStatus) {
    case 'PAID':
      return 'PAID_AT_PARENT';
    case 'DUE':
    case 'PENDING':
      return 'DUE_AT_PARENT';
    case 'UNREGISTERED':
    default:
      return 'UNPAID';
  }
}

function extractApiErrorMessage(raw: string, fallback: string) {
  let current: unknown = raw;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) break;
    if (!(trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"'))) break;
    try {
      current = JSON.parse(trimmed);
    } catch {
      break;
    }
  }

  const message = typeof current === 'string'
    ? current
    : current && typeof current === 'object' && 'message' in current && typeof (current as { message?: unknown }).message === 'string'
      ? (current as { message: string }).message
      : fallback;

  if (message.includes('paymentStatus') && message.includes('Expected')) {
    return 'Treatment could not be saved because the payment status was invalid. Refresh the page and try again.';
  }

  return message || fallback;
}

function inventoryItemLabel(item: InventorySummaryRow) {
  return [item.name, item.strength, item.packageSize].filter(Boolean).join(' - ');
}

function inventoryLotLabel(item: InventorySummaryRow) {
  const parts = [item.lotNumber, item.brand].filter(Boolean);
  const quantity = Number(item.available || 0);
  parts.push(`Available ${quantity}${item.baseUnit ? ` ${item.baseUnit}` : ''}`);
  return parts.join(' - ');
}

function normalizeRecipe(recipe: StockUsage[]) {
  return recipe.map((item) => ({
    itemId: item.itemId || undefined,
    billingProductId: item.billingProductId || undefined,
    lotId: item.lotId || undefined,
    itemName: item.itemName || 'Stock item',
    plannedQuantity: Number(item.plannedQuantity ?? item.actualQuantity ?? 1),
    actualQuantity: Number(item.actualQuantity ?? item.plannedQuantity ?? 1),
    unit: item.unit || '',
    brand: item.brand || '',
    lotNumber: item.lotNumber || '',
    deviationReason: item.deviationReason || '',
  }));
}

function formatMoney(amount: number, currency: string) {
  return formatOrgMoney(amount, { currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPaymentStatus(status: PaymentStatus) {
  return paymentStatusLabels[status];
}

export default function TreatmentCompletionPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { fetchJson, fetchApi } = useApiClient();
  const { serviceVariantsEnabled } = useTreatmentCatalogConfig();
  const [data, setData] = useState<CompletionPayload | null>(null);
  const [inventoryRows, setInventoryRows] = useState<InventorySummaryRow[]>([]);
  const [performedSelection, setPerformedSelection] = useState('');
  const [notes, setNotes] = useState('');
  const [checklist, setChecklist] = useState<Array<{ label: string; checked: boolean }>>([]);
  const [safetyChecks, setSafetyChecks] = useState<Record<string, any>>({});
  const [stockUsage, setStockUsage] = useState<StockUsage[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('UNPAID');
  const [expectedAmount, setExpectedAmount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const inventoryItems = useMemo(() => {
    const seen = new Set<string>();
    return inventoryRows.filter((row) => {
      if (!row.id || seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    });
  }, [inventoryRows]);

  const performedOptions = useMemo<ServicePerformedOption[]>(() => {
    if (!data) return [];

    const options: ServicePerformedOption[] = [
      {
        value: `appointment-type:${data.appointment.appointmentType.id}`,
        label: data.appointment.appointmentType.name,
        supportingText: 'Booked visit',
        price: Number(data.appointment.appointmentType.price ?? 0),
        code: data.appointment.appointmentType.id,
      },
    ];

    if (!serviceVariantsEnabled) {
      return options;
    }

    const seen = new Set<string>();
    const appendVariant = (service: CompletionPayload['appointment']['billingService'] | NonNullable<CompletionPayload['availableServices']>[number] | null | undefined) => {
      if (!service?.id || seen.has(service.id)) return;
      seen.add(service.id);
      options.push({
        value: `billing-service:${service.id}`,
        label: service.name,
        supportingText: service.category ? `${service.category} service option` : 'Service option',
        price: Number(service.price ?? 0),
        code: 'code' in service && service.code ? service.code : service.id,
        billingServiceId: service.id,
      });
    };

    appendVariant(data.appointment.billingService);
    (data.availableServices || []).forEach((service) => appendVariant(service));

    return options;
  }, [data, serviceVariantsEnabled]);

  const selectedPerformedOption = useMemo(() => {
    if (!performedOptions.length) return null;
    return performedOptions.find((option) => option.value === performedSelection) || performedOptions[0];
  }, [performedOptions, performedSelection]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchJson<CompletionPayload>(`/appointments/${id}/treatment-completion`)
      .then(async (payload) => {
        if (!active) return;
        setData(payload);
        const initialBillingServiceId = serviceVariantsEnabled
          ? payload.completion?.billingServiceId || payload.appointment.billingService?.id || ''
          : '';
        const selectedService = (payload.availableServices || []).find((service) => service.id === initialBillingServiceId)
          || (payload.completion?.performedServiceName
            ? (payload.availableServices || []).find((service) => service.name === payload.completion?.performedServiceName)
            : null)
          || null;
        setPerformedSelection(
          selectedService?.id
            ? `billing-service:${selectedService.id}`
            : `appointment-type:${payload.appointment.appointmentType.id}`,
        );
        setNotes(payload.completion?.notes || '');
        setChecklist(
          Array.isArray(payload.completion?.checklist)
            ? payload.completion.checklist.filter((item) => item?.label !== 'Clinical safety checks')
            : [],
        );
        const rules = payload.appointment.appointmentType.clinicalSafetyRules || {};
        if (rules.enabled) {
          setSafetyChecks({
            allergiesReviewed: false,
            medicationsReviewed: false,
            pregnancyReviewed: false,
            contraindicationsReviewed: false,
            suitabilityConfirmed: false,
            doctorEscalated: false,
            adverseEventOccurred: false,
            adverseEventNotes: '',
          });
        }
        setStockUsage(normalizeRecipe(payload.completion?.stockUsage?.length ? payload.completion.stockUsage : payload.defaultStockRecipe));
        setPaymentStatus(normalizeTreatmentPaymentStatus(payload.completion?.paymentStatus || payload.appointment.paymentStatus));
        const defaultPrice = Number(selectedService?.price ?? payload.appointment.billingService?.price ?? payload.appointment.appointmentType.price ?? 0);
        setExpectedAmount(Number(payload.completion?.expectedAmount ?? defaultPrice));
        setFinalAmount(Number(payload.completion?.finalAmount ?? defaultPrice));

        const locationId = payload.appointment.location?.id;
        try {
          const inventoryPayload = await fetchJson<{ data: InventorySummaryRow[] }>(
            locationId ? `/inventory/summary?locationId=${encodeURIComponent(locationId)}` : '/inventory/summary',
          );
          if (active) {
            setInventoryRows(Array.isArray(inventoryPayload.data) ? inventoryPayload.data : []);
          }
        } catch {
          if (active) setInventoryRows([]);
        }
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Unable to load treatment completion.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchJson, id, serviceVariantsEnabled]);

  useEffect(() => {
    if (!performedOptions.length) return;
    if (!performedOptions.some((option) => option.value === performedSelection)) {
      setPerformedSelection(performedOptions[0].value);
    }
  }, [performedOptions, performedSelection]);

  function updateStock(index: number, patch: Partial<StockUsage>) {
    setStockUsage((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  function selectInventoryItem(index: number, itemId: string) {
    const selected = inventoryItems.find((item) => item.id === itemId);
    updateStock(index, {
      itemId: itemId || undefined,
      itemName: selected?.name || '',
      unit: selected?.baseUnit || '',
      lotId: undefined,
      lotNumber: '',
      brand: selected?.brand || '',
    });
  }

  function selectLot(index: number, itemId: string | undefined, lotId: string) {
    if (!itemId) {
      updateStock(index, { lotId: undefined, lotNumber: '' });
      return;
    }
    const selectedLot = inventoryRows.find((row) => row.id === itemId && (row.lotId || '') === lotId);
    updateStock(index, {
      lotId: lotId || undefined,
      lotNumber: selectedLot?.lotNumber || '',
      brand: selectedLot?.brand || '',
    });
  }

  function onPerformedServiceChange(nextValue: string) {
    setPerformedSelection(nextValue);
    const selected = performedOptions.find((option) => option.value === nextValue) || null;
    if (!selected) return;
    setExpectedAmount(selected.price);
    setFinalAmount(selected.price);
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      const preparedStockUsage = stockUsage.filter((item) => item.itemId || item.itemName.trim().length > 0);
      const response = await fetchApi(`/appointments/${id}/treatment-completion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          billingServiceId: serviceVariantsEnabled ? selectedPerformedOption?.billingServiceId || undefined : undefined,
          performedServiceName: selectedPerformedOption?.label || data?.appointment.appointmentType.name || 'Service',
          performedServices: selectedPerformedOption
            ? [{
                serviceId: selectedPerformedOption.billingServiceId,
                code: selectedPerformedOption.code,
                name: selectedPerformedOption.label,
                quantity: 1,
                unitPrice: Number(finalAmount || selectedPerformedOption.price || 0),
              }]
            : undefined,
          notes,
          checklist,
          safetyChecks,
          stockUsage: preparedStockUsage,
          paymentStatus,
          expectedAmount,
          finalAmount,
        }),
      });
      if (!response.ok) throw new Error(extractApiErrorMessage(await response.text(), 'Unable to complete treatment.'));
      navigate('/appointments');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to complete treatment.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-6 text-slate-600">Loading treatment completion...</div>;
  if (!data) return <div className="p-6 text-rose-700">{error || 'Treatment completion could not be loaded.'}</div>;
  const safetyRules = data.appointment.appointmentType.clinicalSafetyRules || {};
  const safetyEnabled = Boolean(safetyRules.enabled);
  const canAddStockUsage = inventoryItems.length > 0;
  const selectedCurrency = data.appointment.appointmentType.currency || 'USD';
  const priceDelta = Number(finalAmount) - Number(expectedAmount);
  const hasVariantChoices = performedOptions.some((option) => Boolean(option.billingServiceId));

  return (
    <div className="space-y-6 p-6">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-sky-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Treatment completion</p>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900">{data.appointment.patient.name}</h1>
              <p className="mt-1 text-sm text-slate-600">{data.appointment.location?.name || 'No location'} · {data.appointment.provider.name}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs font-medium">
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700">Booked visit: {data.appointment.appointmentType.name}</span>
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700">Delivered service: {hasVariantChoices ? 'Choose below' : 'Booked service only'}</span>
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700">Status: {data.appointment.status}</span>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_220px]">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Service outcome</p>
                  <p className="mt-1 text-sm text-slate-500">
                    {serviceVariantsEnabled && performedOptions.length > 1
                      ? 'Choose the exact treatment that was delivered for this appointment.'
                      : 'This appointment will be completed using the booked service.'}
                  </p>
                </div>
                <label className="block text-sm">
                  <span className="mb-2 block font-medium text-slate-700">Service performed</span>
                  <select
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm shadow-sm"
                    value={selectedPerformedOption?.value || ''}
                    onChange={(event) => onPerformedServiceChange(event.target.value)}
                    disabled={performedOptions.length <= 1}
                  >
                    {performedOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} · {option.supportingText} · {formatMoney(option.price, selectedCurrency)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-950 p-4 text-white shadow-inner">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Selected price</p>
                <p className="mt-3 text-2xl font-semibold">{formatMoney(selectedPerformedOption?.price ?? 0, selectedCurrency)}</p>
                <p className="mt-2 text-sm text-slate-300">Source: {selectedPerformedOption?.supportingText || 'Booked visit'}.</p>
                <p className="mt-1 text-xs text-slate-400">Completion pricing defaults from the appointment type or selected service option price.</p>
              </div>
            </div>
          </div>

          {safetyEnabled ? (
            <div className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
              <p className="text-sm font-semibold text-amber-900">Clinical safety checks</p>
              {[
                ['requireAllergyReview', 'allergiesReviewed', 'Allergies reviewed'],
                ['requireMedicationReview', 'medicationsReviewed', 'Medications reviewed'],
                ['requirePregnancyReview', 'pregnancyReviewed', 'Pregnancy status reviewed'],
                ['requireContraindicationReview', 'contraindicationsReviewed', 'Contraindications reviewed'],
                ['requireSuitabilityConfirmation', 'suitabilityConfirmed', 'Patient suitability confirmed'],
                ['doctorEscalationRequired', 'doctorEscalated', 'Doctor or medical director escalation completed'],
              ].filter(([ruleKey]) => safetyRules[ruleKey]).map(([, checkKey, label]) => (
                <label key={checkKey} className="flex items-center gap-2 text-sm text-amber-900">
                  <input type="checkbox" checked={Boolean(safetyChecks[checkKey])} onChange={(event) => setSafetyChecks((prev) => ({ ...prev, [checkKey]: event.target.checked }))} />
                  {label}
                </label>
              ))}
              {safetyRules.adverseEventRecordingEnabled ? (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-amber-900">
                    <input type="checkbox" checked={Boolean(safetyChecks.adverseEventOccurred)} onChange={(event) => setSafetyChecks((prev) => ({ ...prev, adverseEventOccurred: event.target.checked }))} />
                    Adverse event occurred
                  </label>
                  {safetyChecks.adverseEventOccurred ? (
                    <textarea className="min-h-20 w-full rounded-md border border-amber-300 px-3 py-2 text-sm" placeholder="Adverse event notes" value={safetyChecks.adverseEventNotes || ''} onChange={(event) => setSafetyChecks((prev) => ({ ...prev, adverseEventNotes: event.target.value }))} />
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Stock usage</p>
                <p className="mt-1 text-sm text-slate-500">Record actual inventory consumed for this treatment.</p>
              </div>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setStockUsage((current) => [...current, { itemName: '', plannedQuantity: 0, actualQuantity: 1 }])}
                disabled={!canAddStockUsage}
                title={canAddStockUsage ? 'Add inventory usage row' : 'Inventory options are unavailable for this appointment location.'}
              >
                <Plus size={14} />
                Add usage
              </button>
            </div>
            {canAddStockUsage ? (
              <p className="text-sm text-slate-500">Pick the actual inventory item used so treatment completion can deduct clinic stock accurately.</p>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Inventory options are unavailable for this appointment location. Stock deduction cannot be added here until the appointment has a location with inventory access.
              </div>
            )}
            {!stockUsage.length && !canAddStockUsage ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                No stock usage can be recorded from this screen until inventory is available for the appointment location.
              </div>
            ) : null}
            {stockUsage.map((item, index) => (
              <div key={index} className="grid gap-3 rounded-2xl border border-slate-200 p-4 md:grid-cols-[1.35fr_1.3fr_110px_110px_48px]">
                <select
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={item.itemId || ''}
                  onChange={(event) => selectInventoryItem(index, event.target.value)}
                >
                  <option value="">Type item manually</option>
                  {inventoryItems.map((inventoryItem) => (
                    <option key={inventoryItem.id} value={inventoryItem.id}>{inventoryItemLabel(inventoryItem)}</option>
                  ))}
                </select>
                <input className="rounded-xl border border-slate-300 px-3 py-2 text-sm" placeholder="Item" value={item.itemName} onChange={(event) => updateStock(index, { itemName: event.target.value })} />
                <input className="rounded-xl border border-slate-300 px-3 py-2 text-sm" type="number" min={0} placeholder="Actual" value={item.actualQuantity} onChange={(event) => updateStock(index, { actualQuantity: Number(event.target.value) })} />
                <input className="rounded-xl border border-slate-300 px-3 py-2 text-sm" placeholder="Unit" value={item.unit || ''} onChange={(event) => updateStock(index, { unit: event.target.value })} />
                <button type="button" className="inline-flex items-center justify-center rounded-xl border border-slate-200 text-slate-500" onClick={() => setStockUsage((current) => current.filter((_, i) => i !== index))}>
                  <Trash2 size={14} />
                </button>
                <div className="space-y-2 md:col-span-2">
                  <select
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    value={item.lotId || ''}
                    onChange={(event) => selectLot(index, item.itemId, event.target.value)}
                    disabled={!item.itemId || !inventoryRows.some((row) => row.id === item.itemId && (row.lotId || row.lotNumber))}
                  >
                    <option value="">Use oldest available lot automatically</option>
                    {inventoryRows
                      .filter((row) => row.id === item.itemId && (row.lotId || row.lotNumber))
                      .map((row) => (
                        <option key={row.lotId || `${row.id}-${row.lotNumber || 'default'}`} value={row.lotId || ''}>{inventoryLotLabel(row)}</option>
                      ))}
                  </select>
                </div>
                <div className="rounded-xl bg-slate-50 px-3 py-3 text-xs text-slate-600 md:col-span-2">
                  Planned {item.plannedQuantity ?? item.actualQuantity} {item.unit || ''}
                  {item.lotNumber ? ` · Lot ${item.lotNumber}` : ''}
                  {item.brand ? ` · ${item.brand}` : ''}
                </div>
                {item.plannedQuantity !== undefined && item.actualQuantity !== item.plannedQuantity ? (
                  <input className="rounded-xl border border-amber-300 px-3 py-2 text-sm md:col-span-6" placeholder="Deviation reason" value={item.deviationReason || ''} onChange={(event) => updateStock(index, { deviationReason: event.target.value })} />
                ) : null}
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <label className="block text-sm">
              <span className="mb-2 block font-semibold text-slate-900">Completion notes</span>
              <textarea className="min-h-32 w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm" placeholder="Capture clinically useful treatment notes, outcome details, or follow-up information." value={notes} onChange={(event) => setNotes(event.target.value)} />
            </label>
          </div>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 space-y-1">
              <p className="text-sm font-semibold text-slate-900">Payment summary</p>
              <p className="text-sm text-slate-500">Amounts default from the scheduled service price and can be adjusted at completion time.</p>
            </div>
            <div className="space-y-4">
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-slate-700">Payment status</span>
                <select className="w-full rounded-xl border border-slate-300 px-3 py-3" value={paymentStatus} onChange={(event) => setPaymentStatus(event.target.value as PaymentStatus)}>
                  {paymentStatuses.map((status) => (
                    <option key={status} value={status}>{formatPaymentStatus(status)}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-slate-700">Appointment amount</span>
                <input className="w-full rounded-xl border border-slate-300 px-3 py-3" type="number" min={0} value={expectedAmount} onChange={(event) => setExpectedAmount(Number(event.target.value))} />
              </label>
              <label className="block text-sm">
                <span className="mb-2 block font-medium text-slate-700">Amount to bill</span>
                <input className="w-full rounded-xl border border-slate-300 px-3 py-3" type="number" min={0} value={finalAmount} onChange={(event) => setFinalAmount(Number(event.target.value))} />
              </label>
            </div>

            <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
              <div className="flex items-center justify-between">
                <span>Base price</span>
                <span className="font-medium">{formatMoney(selectedPerformedOption?.price ?? 0, selectedCurrency)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span>Expected</span>
                <span className="font-medium">{formatMoney(expectedAmount, selectedCurrency)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span>Final</span>
                <span className="font-medium">{formatMoney(finalAmount, selectedCurrency)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span>Adjustment</span>
                <span className={priceDelta === 0 ? 'text-slate-500' : priceDelta > 0 ? 'text-emerald-700' : 'text-amber-700'}>
                  {priceDelta === 0 ? 'No adjustment' : `${priceDelta > 0 ? '+' : '-'}${formatMoney(Math.abs(priceDelta), selectedCurrency)}`}
                </span>
              </div>
            </div>

            <button type="button" disabled={saving} onClick={() => void save()} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-sm disabled:opacity-50">
              <CheckCircle2 size={16} />
              {saving ? 'Completing...' : 'Complete treatment and return to appointments'}
            </button>
          </div>
        </aside>
      </section>
    </div>
  );
}
